// Headless game core. Owns the rules; knows nothing about DOM, canvas or audio.
// The browser view and the simulator both drive this same class.
import { D, Decimal, ONE, ZERO } from '../core/decimal';
import { Emitter } from '../core/events';
import type { Rng } from '../core/rng';
import { ARTIFACTS, ARTIFACT_BY_ID } from '../data/artifacts';
import { BALANCE } from '../data/balance';
import { difficultyOf, type DifficultyDef, type DifficultyId } from '../data/difficulty';
import { HEROES, HERO_BY_ID, type HeroDef, type HeroSkill } from '../data/heroes';
import { MANA, SKILLS, SKILL_BY_ID, SKILL_POWER, type SkillDef, type SkillId } from '../data/skills';
import {
  bossHp, heroAffordable, heroCost, heroDps, monsterGold, monsterHp,
  relicsForStage, tapAffordable, tapBase, tapCost,
} from './formulas';
import { artifactAffordable, artifactCost, artifactSource, discoveryCost } from './artifacts';
import { heroSkillSource, selfSkillMult, skillsCrossed } from './heroSkills';
import { availableSkillPoints, nodeBlocked, skillTreeSource } from './skillTree';
import { equipmentSource, itemScore, rollItem, type Item } from './equipment';
import { PETS, PET_BY_ID, PET_TUNING, petLevelCost, type PetDef } from '../data/pets';
import { petAttackMult, petSource } from './pets';
import { Meta, milestoneSource, type RewardResult } from './meta';
import type { BoostId, Reward } from '../data/meta';
import { EQUIPMENT_TUNING, RARITIES, type Rarity } from '../data/equipment';
import { NODE_BY_ID, type TreeNode } from '../data/skillTree';
import { freshRun, newState, type GameState } from './state';
import { Stats } from './stats';

export interface Pos { x: number; y: number }

export interface Monster {
  hp: Decimal;
  maxHp: Decimal;
  boss: boolean;
  /** Stable per-monster seed for procedural looks. */
  seed: number;
  dead: boolean;
}

export type DamageSource = 'tap' | 'auto' | 'skill' | 'pet';

export type GameEvents = {
  spawn: { monster: Monster };
  damage: { amount: Decimal; crit: boolean; source: DamageSource; pos?: Pos };
  kill: { monster: Monster; gold: Decimal };
  stageCleared: { stage: number };
  bossFailed: Record<string, never>;
  heroHired: { hero: HeroDef };
  heroSkillUnlocked: { hero: HeroDef; skill: HeroSkill };
  purchase: { kind: 'hero' | 'tap'; id?: string; count: number; cost: Decimal };
  skillUsed: { skill: SkillDef };
  skillLeveled: { skill: SkillDef; level: number; cost: Decimal };
  nodeLeveled: { node: TreeNode; level: number };
  itemDropped: { item: Item; kept: boolean; autoEquipped: boolean };
  petFood: { pet: PetDef; amount: number };
  petLeveled: { pet: PetDef; level: number };
  petAttack: { pet: PetDef };
  rewardGranted: { source: string; reward: Reward; result: RewardResult };
  supplyDropSpawned: Record<string, never>;
  supplyDropMissed: Record<string, never>;
  boostStarted: { boost: BoostId };
  treeRespec: Record<string, never>;
  prestige: { relicsGained: Decimal; stars: Record<string, number> };
  artifactDiscovered: { id: string };
  artifactLeveled: { id: string; count: number; cost: Decimal };
  offline: { seconds: number; gold: Decimal };
  purchaseCredited: { orderId: string; gems: number };
};

export type BuyMode = number | 'max';
export interface Quote { count: number; cost: Decimal; affordable: boolean }

export interface GameOptions {
  state?: GameState;
  /** Epoch-ms clock. The simulator substitutes a virtual one. */
  now?: () => number;
  rng?: Rng;
  difficulty?: DifficultyId;
}

export class Game {
  state: GameState;
  readonly events = new Emitter<GameEvents>();
  readonly stats: Stats;
  /** Phase 2 retention systems (Power Cells, daily, directives, milestones, drops, shop). */
  readonly meta: Meta;
  readonly now: () => number;
  readonly difficulty: DifficultyDef;
  private rng: Rng;

  monster!: Monster;
  bossTimeLeft = 0;
  private respawnIn = 0;
  private frenzyAcc = 0;
  private heroDpsCache: Decimal | null = null;

  constructor(opts: GameOptions = {}) {
    this.now = opts.now ?? Date.now;
    this.rng = opts.rng ?? Math.random;
    this.difficulty = difficultyOf(opts.difficulty);
    this.state = opts.state ?? newState(this.now());
    this.stats = new Stats(() => this.state);
    this.stats.register(artifactSource());
    this.stats.register(heroSkillSource());
    this.stats.register(skillTreeSource());
    this.stats.register(equipmentSource());
    this.stats.register(petSource());
    this.stats.register(milestoneSource());
    this.stats.register({
      id: 'forge',
      apply: (_s, m) => { if (this.forgeLevel) m.mult('allDamage', D(1 + BALANCE.coinPower.forgeDamagePerLevel).pow(this.forgeLevel)); },
    });
    this.meta = new Meta(this);
    this.spawnMonster();
  }

  // ---- Derived values ------------------------------------------------------------

  heroLevel(id: string): number {
    return this.state.heroLevels[id] ?? 0;
  }

  /**
   * One hero's DPS including all multipliers (excluding temporary skill buffs).
   * For a hypothetical `level` (upgrade previews, bot), swaps in the self-skills it would have then.
   */
  heroDpsOf(hero: HeroDef, level = this.heroLevel(hero.id)): Decimal {
    let self = this.stats.mult(`heroSelf.${hero.id}`);
    const current = this.heroLevel(hero.id);
    if (level !== current) self = self.mul(selfSkillMult(hero, level) / selfSkillMult(hero, current));
    return heroDps(hero, level)
      .mul(self)
      .mul(this.starMult(hero.id))
      .mul(this.stats.mult('heroDamage'))
      .mul(this.stats.mult(`heroDamage.${hero.type}`))
      .mul(this.stats.mult('allDamage'));
  }

  // ---- Guardian Stars (BALANCE.stars) ---------------------------------------------------------

  heroStars(id: string): number { return this.state.heroStars[id] ?? 0; }

  /** Damage multiplier from a Guardian's stars (1 when Stars are off or it has none). */
  starMult(id: string): Decimal {
    const { mult } = BALANCE.stars, n = this.heroStars(id);
    return mult > 0 && n > 0 ? D(mult).pow(n) : ONE;
  }

  /** Level a Guardian needs this run for its next star. */
  nextStarLevel(id: string): number {
    const { firstLevel, levelStep } = BALANCE.stars;
    return firstLevel + this.heroStars(id) * levelStep;
  }

  /** Stars each Guardian would earn if the player Rewound now (id -> count). */
  starsOnPrestige(): Record<string, number> {
    const out: Record<string, number> = {};
    if (BALANCE.stars.mult <= 0) return out;
    const { firstLevel, levelStep } = BALANCE.stars;
    for (const h of HEROES) {
      const lvl = this.heroLevel(h.id), have = this.heroStars(h.id);
      if (lvl < firstLevel) continue;
      const total = Math.floor((lvl - firstLevel) / levelStep) + 1;
      if (total > have) out[h.id] = total - have;
    }
    return out;
  }

  /** Total hero DPS, excluding temporary skill buffs. Cached until purchases/stats change. */
  heroDpsTotal(): Decimal {
    if (!this.heroDpsCache) {
      let sum = ZERO;
      for (const h of HEROES) if (this.heroLevel(h.id) > 0) sum = sum.add(this.heroDpsOf(h));
      this.heroDpsCache = sum;
    }
    return this.heroDpsCache;
  }

  /** Random number from the game's (seedable) RNG, for systems outside this class. */
  random(): number { return this.rng(); }

  /** Shop boosts (time-limited, bought with Power Cells). */
  private boostMult(id: BoostId): number { return this.meta.boostActive(id) ? 2 : 1; }

  /** Hero DPS right now, including active buffs. */
  heroDpsNow(): Decimal {
    const dps = this.skillActive('warcry') ? this.heroDpsTotal().mul(this.warcryMult()) : this.heroDpsTotal();
    return dps.mul(this.boostMult('damage2x'));
  }

  /** Tap damage from permanent sources (used for upgrade previews). */
  tapDamage(level = this.state.tapLevel): Decimal {
    const own = tapBase(level).mul(this.stats.mult('tapDamage')).mul(this.stats.mult('allDamage'));
    return own.add(this.heroDpsTotal().mul(this.tapDpsShare()));
  }

  /** Tap damage right now, including Fire Sword. */
  tapDamageNow(): Decimal {
    const tap = this.skillActive('firesword') ? this.tapDamage().mul(this.fireswordMult()) : this.tapDamage();
    return tap.mul(this.boostMult('damage2x'));
  }

  tapDpsShare(): number { return BALANCE.tapDpsShare + this.stats.add('tapDpsShare'); }
  critChance(): number {
    const deadly = this.skillActive('deadly') ? SKILL_POWER.deadlyCritChance(this.skillLevel('deadly')) : 0;
    return Math.min(1, BALANCE.critChance + this.stats.add('critChance') + deadly);
  }
  critMult(): Decimal {
    const m = D(BALANCE.critMult).mul(this.stats.mult('critDamage'));
    return this.skillActive('deadly') ? m.mul(SKILL_POWER.deadlyCritMult(this.skillLevel('deadly'))) : m;
  }
  bossTimeSec(): number { return BALANCE.bossTimeSec + this.stats.add('bossTimeSec'); }

  // Skill power at the current skill level, after artifact (and later skill-tree) bonuses.
  warcryMult(): number { return SKILL_POWER.warcryMult(this.skillLevel('warcry')) + this.stats.add('warcryBonus'); }
  frenzyRate(): number { return SKILL_POWER.frenzyTapsPerSec(this.skillLevel('frenzy')) + this.stats.add('frenzyRate'); }
  midasMult(): Decimal { return D(SKILL_POWER.midasMult(this.skillLevel('midas'))).mul(this.stats.mult('midasGold')); }
  fireswordMult(): number { return SKILL_POWER.fireswordMult(this.skillLevel('firesword')) + this.stats.add('fireswordBonus'); }
  strikeDamage(): Decimal {
    return this.tapDamageNow().mul(SKILL_POWER.strikeTaps(this.skillLevel('strike'))).mul(this.stats.mult('strikeDamage'));
  }
  skillDurationSec(skill: SkillDef): number { return skill.durationSec * (1 + this.stats.add('skillDurationPct')); }

  // ---- Difficulty-adjusted economy ---------------------------------------------------

  monsterHpAt(stage: number): Decimal {
    const d = this.difficulty;
    const hp = monsterHp(stage).mul(d.hpMult);
    return d.hpGrowth === 1 ? hp : hp.mul(Decimal.pow(d.hpGrowth, stage - 1)).ceil();
  }

  bossHpAt(stage: number): Decimal { return this.monsterHpAt(stage).mul(BALANCE.bossHpMult); }

  /** Gold per normal kill before bonuses. Deliberately not scaled by difficulty HP. */
  goldAt(stage: number): Decimal { return monsterGold(stage).mul(this.difficulty.goldMult); }

  killGold(boss: boolean): Decimal {
    let g = this.goldAt(this.state.stage).mul(this.stats.mult('gold'));
    if (boss) g = g.mul(BALANCE.bossGoldMult).mul(this.stats.mult('bossGold'));
    if (this.skillActive('midas')) g = g.mul(this.midasMult());
    return g.mul(this.boostMult('scrap2x'));
  }

  // ---- Simulation step ---------------------------------------------------------------

  update(dt: number): void {
    this.state.lifetime.playSec += dt;
    this.state.mana = Math.min(this.maxMana(), this.state.mana + this.manaRegen() * dt);
    this.meta.update();
    if (this.monster.dead) {
      this.respawnIn -= dt;
      if (this.respawnIn <= 0) this.spawnMonster();
      return;
    }
    const dps = this.heroDpsNow();
    if (dps.gt(0)) this.applyDamage(dps.mul(dt), false, null);
    if (this.monster.dead) return;

    if (this.monster.boss) {
      this.bossTimeLeft -= dt;
      if (this.bossTimeLeft <= 0) { this.failBoss(); return; }
    }
    if (this.skillActive('frenzy')) {
      this.frenzyAcc += dt * this.frenzyRate();
      while (this.frenzyAcc >= 1 && !this.monster.dead) {
        this.frenzyAcc -= 1;
        this.tap(undefined, 'auto');
      }
    }
  }

  // ---- Player actions -------------------------------------------------------------------

  tap(pos?: Pos, source: DamageSource = 'tap'): void {
    if (this.monster.dead) return;
    const crit = this.rng() < this.critChance();
    const amount = crit ? this.tapDamageNow().mul(this.critMult()) : this.tapDamageNow();
    if (source === 'tap') this.state.lifetime.taps++;
    this.applyDamage(amount, crit, source, pos);
    this.tickPet();
  }

  /** Retry the boss after it escaped. */
  fightBoss(): void {
    if (!this.state.farming) return;
    this.state.farming = false;
    this.state.kills = BALANCE.monstersPerStage;
    if (!this.monster.dead) this.spawnMonster();
  }

  quoteHero(id: string, mode: BuyMode): Quote {
    const hero = HERO_BY_ID.get(id)!;
    const lvl = this.heroLevel(id);
    const costMult = this.stats.mult('heroCost').mul(this.difficulty.costMult);
    const count = mode === 'max' ? Math.max(1, heroAffordable(hero, lvl, this.state.gold.div(costMult))) : mode;
    const cost = heroCost(hero, lvl, count).mul(costMult);
    return { count, cost, affordable: cost.lte(this.state.gold) };
  }

  buyHero(id: string, mode: BuyMode): boolean {
    const hero = HERO_BY_ID.get(id);
    if (!hero || !this.heroUnlocked(hero)) return false;
    const q = this.quoteHero(id, mode);
    if (!q.affordable) return false;
    const before = this.heroLevel(id);
    this.state.gold = this.state.gold.sub(q.cost);
    this.state.heroLevels[id] = before + q.count;
    const crossed = skillsCrossed(hero, before, before + q.count);
    // Hero skills feed the stat pipeline, so a threshold crossing must invalidate stats too.
    if (crossed.length) this.invalidate(); else this.heroDpsCache = null;
    if (before === 0) this.events.emit('heroHired', { hero });
    for (const skill of crossed) this.events.emit('heroSkillUnlocked', { hero, skill });
    this.events.emit('purchase', { kind: 'hero', id, count: q.count, cost: q.cost });
    return true;
  }

  /** A hero can be hired once the previous one is owned. */
  heroUnlocked(hero: HeroDef): boolean {
    const i = HEROES.indexOf(hero);
    return i <= 0 || this.heroLevel(HEROES[i - 1]!.id) > 0;
  }

  quoteTap(mode: BuyMode): Quote {
    const lvl = this.state.tapLevel;
    const costMult = this.stats.mult('tapCost').mul(this.difficulty.costMult);
    const count = mode === 'max' ? Math.max(1, tapAffordable(lvl, this.state.gold.div(costMult))) : mode;
    const cost = tapCost(lvl, count).mul(costMult);
    return { count, cost, affordable: cost.lte(this.state.gold) };
  }

  buyTap(mode: BuyMode): boolean {
    const q = this.quoteTap(mode);
    if (!q.affordable) return false;
    this.state.gold = this.state.gold.sub(q.cost);
    this.state.tapLevel += q.count;
    this.events.emit('purchase', { kind: 'tap', count: q.count, cost: q.cost });
    return true;
  }

  // ---- Skills -----------------------------------------------------------------------------

  skillUnlocked(skill: SkillDef): boolean { return this.state.bestStage >= skill.unlockStage; }
  skillLevel(id: SkillId): number { return this.state.skillLevels[id] ?? 0; }

  skillActive(id: SkillId): boolean {
    return (this.state.skills[id]?.activeUntil ?? 0) > this.now();
  }

  skillActiveLeft(id: SkillId): number {
    return Math.max(0, ((this.state.skills[id]?.activeUntil ?? 0) - this.now()) / 1000);
  }

  maxMana(): number {
    const levels = SKILLS.reduce((n, s) => n + this.skillLevel(s.id), 0);
    return MANA.base + MANA.perSkillLevel * levels + this.stats.add('maxMana');
  }

  manaRegen(): number { return MANA.regenPerSec * (1 + this.stats.add('manaRegenPct')); }

  /** Why a skill can't be cast right now, or null if it can. */
  skillBlocked(id: SkillId): 'locked' | 'unlearned' | 'active' | 'mana' | null {
    const skill = SKILL_BY_ID.get(id)!;
    if (!this.skillUnlocked(skill)) return 'locked';
    if (this.skillLevel(id) <= 0) return 'unlearned';
    if (this.skillActive(id)) return 'active';
    if (this.state.mana < skill.manaCost) return 'mana';
    return null;
  }

  useSkill(id: SkillId): boolean {
    const skill = SKILL_BY_ID.get(id);
    if (!skill || this.skillBlocked(id)) return false;
    this.state.mana -= skill.manaCost;
    const t = this.now();
    this.state.skills[id] = { readyAt: t, activeUntil: t + this.skillDurationSec(skill) * 1000 };
    this.events.emit('skillUsed', { skill });
    if (id === 'strike' && !this.monster.dead) this.applyDamage(this.strikeDamage(), true, 'skill');
    return true;
  }

  /** Gold price of the next skill level, or null at max level. */
  skillLevelCost(id: SkillId): Decimal | null {
    const skill = SKILL_BY_ID.get(id)!;
    const lvl = this.skillLevel(id);
    if (lvl >= skill.maxLevel) return null;
    return monsterGold(skill.unlockStage + lvl * skill.stagesPerLevel).mul(skill.goldMult)
      .mul(this.stats.mult('tapCost')).mul(this.difficulty.costMult);
  }

  /** Levels are gated by best stage as well as gold: level n needs the stage its price is pegged to. */
  skillLevelStageReq(id: SkillId): number {
    const skill = SKILL_BY_ID.get(id)!;
    return skill.unlockStage + this.skillLevel(id) * skill.stagesPerLevel;
  }

  levelSkill(id: SkillId): boolean {
    const skill = SKILL_BY_ID.get(id);
    const cost = skill && this.skillLevelCost(id);
    if (!skill || !cost || cost.gt(this.state.gold) || this.state.bestStage < this.skillLevelStageReq(id)) return false;
    this.state.gold = this.state.gold.sub(cost);
    const level = this.skillLevel(id) + 1;
    this.state.skillLevels[id] = level;
    if (level === 1) this.state.mana = Math.min(this.maxMana(), this.state.mana + skill.manaCost); // first cast on the house
    this.events.emit('skillLeveled', { skill, level, cost });
    return true;
  }

  // ---- Skill tree ----------------------------------------------------------------------------

  skillPoints(): number { return availableSkillPoints(this.state); }
  nodeLevel(id: string): number { return this.state.skillTree[id] ?? 0; }

  levelNode(id: string): boolean {
    const node = NODE_BY_ID.get(id);
    if (!node || nodeBlocked(this.state, id)) return false;
    const level = this.nodeLevel(id) + 1;
    this.state.skillTree[id] = level;
    this.invalidate();
    this.events.emit('nodeLeveled', { node, level });
    return true;
  }

  /** Refund every skill point (free). */
  respecTree(): void {
    if (Object.keys(this.state.skillTree).length === 0) return;
    this.state.skillTree = {};
    this.state.mana = Math.min(this.state.mana, this.maxMana());
    this.invalidate();
    this.events.emit('treeRespec', {});
  }

  // ---- Equipment -----------------------------------------------------------------------------

  private rollBossDrop(stage: number, firstClear: boolean): void {
    const guaranteed = firstClear && stage % EQUIPMENT_TUNING.guaranteedEvery === 0;
    if (!guaranteed && this.rng() >= EQUIPMENT_TUNING.bossDropChance) return;
    const eq = this.state.equipment;
    const item = rollItem(stage, this.rng, eq.nextUid++, guaranteed ? 'rare' : 'common');
    this.addItem(item);
  }

  /** Add to inventory; auto-equips into an empty slot; when full, keeps the better of new vs worst spare. */
  addItem(item: Item): void {
    const eq = this.state.equipment;
    let kept = true, autoEquipped = false;
    if (eq.items.length >= EQUIPMENT_TUNING.inventoryCap) {
      const equippedUids = new Set(Object.values(eq.equipped));
      const spares = eq.items.filter((i) => !equippedUids.has(i.uid));
      const worst = spares.reduce<Item | null>((w, i) => (!w || itemScore(i) < itemScore(w) ? i : w), null);
      if (worst && itemScore(worst) < itemScore(item)) eq.items.splice(eq.items.indexOf(worst), 1);
      else kept = false;
    }
    if (kept) {
      eq.items.push(item);
      if (eq.equipped[item.slot] === undefined) {
        eq.equipped[item.slot] = item.uid;
        autoEquipped = true;
        this.invalidate();
      }
    }
    this.events.emit('itemDropped', { item, kept, autoEquipped });
  }

  equip(uid: number): boolean {
    const item = this.state.equipment.items.find((i) => i.uid === uid);
    if (!item) return false;
    this.state.equipment.equipped[item.slot] = uid;
    this.invalidate();
    return true;
  }

  unequip(uid: number): boolean {
    const eq = this.state.equipment;
    const item = eq.items.find((i) => i.uid === uid);
    if (!item || eq.equipped[item.slot] !== uid) return false;
    delete eq.equipped[item.slot];
    this.invalidate();
    return true;
  }

  isEquipped(uid: number): boolean {
    return Object.values(this.state.equipment.equipped).includes(uid);
  }

  /** Remove unequipped items. With `belowRarity`, only those strictly below that rarity. */
  salvage(uids: number[]): number {
    const eq = this.state.equipment;
    const drop = new Set(uids.filter((u) => !this.isEquipped(u)));
    const before = eq.items.length;
    eq.items = eq.items.filter((i) => !drop.has(i.uid));
    return before - eq.items.length;
  }

  salvageBelow(rarity: Rarity): number {
    const rank = (r: Rarity) => RARITIES.findIndex((x) => x.id === r);
    return this.salvage(this.state.equipment.items.filter((i) => rank(i.rarity) < rank(rarity)).map((i) => i.uid));
  }

  // ---- Pets ----------------------------------------------------------------------------------

  petLevel(id: string): number { return this.state.pets.levels[id] ?? 0; }
  petFood(id: string): number { return this.state.pets.food[id] ?? 0; }

  /** Food needed for the next level (unlocking a pet costs 1). */
  petNextCost(id: string): number { return petLevelCost(this.petLevel(id)); }

  levelPet(id: string): boolean {
    const pet = PET_BY_ID.get(id);
    const cost = this.petNextCost(id);
    if (!pet || this.petFood(id) < cost) return false;
    const p = this.state.pets;
    p.food[id] = this.petFood(id) - cost;
    p.levels[id] = this.petLevel(id) + 1;
    if (!p.active) p.active = id; // first pet becomes active automatically
    this.invalidate();
    this.events.emit('petLeveled', { pet, level: p.levels[id]! });
    return true;
  }

  setActivePet(id: string): boolean {
    if (this.petLevel(id) <= 0) return false;
    this.state.pets.active = id;
    this.state.pets.tapCounter = 0;
    this.invalidate();
    return true;
  }

  petStrikeDamage(): Decimal {
    const id = this.state.pets.active;
    return id ? this.tapDamageNow().mul(petAttackMult(this.petLevel(id))) : ZERO;
  }

  private tickPet(): void {
    const p = this.state.pets;
    if (!p.active || this.monster.dead) return;
    if (++p.tapCounter < PET_TUNING.tapsPerAttack) return;
    p.tapCounter = 0;
    this.events.emit('petAttack', { pet: PET_BY_ID.get(p.active)! });
    this.applyDamage(this.petStrikeDamage(), false, 'pet');
  }

  private rollPetFood(stage: number, firstClear: boolean): void {
    let amount = 0;
    if (firstClear && stage % PET_TUNING.guaranteedEvery === 0) amount++;
    if (this.rng() < PET_TUNING.foodChance) amount += 1 + Math.floor(this.rng() * PET_TUNING.maxDrop);
    if (amount === 0) return;
    const pet = PETS[Math.floor(this.rng() * PETS.length)]!;
    this.state.pets.food[pet.id] = this.petFood(pet.id) + amount;
    this.events.emit('petFood', { pet, amount });
  }

  // ---- Prestige ------------------------------------------------------------------------------

  relicsOnPrestige(): Decimal {
    const base = relicsForStage(this.state.stage);
    return base.gt(0) ? base.mul(this.stats.mult('relicGain')).mul(this.boughtCoinsBonus()).floor() : base;
  }

  /** Rewind Stardust multiplier from coins bought with purchased Gems this run (1 = none). */
  boughtCoinsBonus(): number {
    return 1 + BALANCE.coinPower.stardustPerDecade * Math.log10(1 + this.state.runBoughtKills / 1000);
  }

  // ---- Treasure Forge (EXPERIMENT, off: BALANCE.coinPower.forgeDamagePerLevel = 0; not saved) -------

  /** Treasure Forge level: permanent all damage, bought with coins, kept through Rewinds. */
  forgeLevel = 0;

  forgeCost(): Decimal {
    const c = BALANCE.coinPower;
    return this.goldAt(c.forgeBaseStage + this.forgeLevel * c.forgeStagesPerLevel).mul(c.forgeKills);
  }

  /** Buys one Forge level if the coins allow (and the Forge is switched on). */
  forgeUp(): boolean {
    if (BALANCE.coinPower.forgeDamagePerLevel <= 0) return false;
    const cost = this.forgeCost();
    if (this.state.gold.lt(cost)) return false;
    this.state.gold = this.state.gold.sub(cost);
    this.forgeLevel++;
    this.invalidate();
    return true;
  }

  prestige(): boolean {
    const gained = this.relicsOnPrestige();
    if (gained.lte(0)) return false;
    const stars = this.starsOnPrestige();
    for (const [id, n] of Object.entries(stars)) this.state.heroStars[id] = this.heroStars(id) + n;
    Object.assign(this.state, freshRun());
    this.state.relics = this.state.relics.add(gained);
    this.state.ascensions++;
    this.state.skills = {};
    this.state.mana = this.maxMana();
    this.invalidate();
    this.spawnMonster();
    this.events.emit('prestige', { relicsGained: gained, stars });
    return true;
  }

  // ---- Artifacts ---------------------------------------------------------------------------

  artifactLevel(id: string): number { return this.state.artifacts[id] ?? 0; }
  artifactsOwned(): number { return Object.keys(this.state.artifacts).length; }
  allArtifactsOwned(): boolean { return this.artifactsOwned() >= ARTIFACTS.length; }
  discoveryCost(): Decimal { return discoveryCost(this.artifactsOwned()); }

  /** Spend relics to discover a random artifact the player doesn't own yet. */
  discoverArtifact(): string | null {
    const pool = ARTIFACTS.filter((a) => !(a.id in this.state.artifacts));
    const cost = this.discoveryCost();
    if (pool.length === 0 || cost.gt(this.state.relics)) return null;
    const def = pool[Math.floor(this.rng() * pool.length)]!;
    this.state.relics = this.state.relics.sub(cost);
    this.state.artifacts[def.id] = 1;
    this.invalidate();
    this.events.emit('artifactDiscovered', { id: def.id });
    return def.id;
  }

  quoteArtifact(id: string, mode: BuyMode): Quote {
    const def = ARTIFACT_BY_ID.get(id)!;
    const lvl = this.artifactLevel(id);
    const count = mode === 'max' ? Math.max(1, artifactAffordable(def, lvl, this.state.relics)) : mode;
    const cost = artifactCost(def, lvl, count);
    return { count, cost, affordable: cost.lte(this.state.relics) };
  }

  levelArtifact(id: string, mode: BuyMode): boolean {
    if (!(id in this.state.artifacts)) return false;
    const q = this.quoteArtifact(id, mode);
    if (!q.affordable) return false;
    this.state.relics = this.state.relics.sub(q.cost);
    this.state.artifacts[id] = this.artifactLevel(id) + q.count;
    this.invalidate();
    this.events.emit('artifactLeveled', { id, count: q.count, cost: q.cost });
    return true;
  }

  // ---- Offline -----------------------------------------------------------------------------

  /** Award idle gold for `seconds` away. Heroes farm the current stage; no stage progress. */
  /**
   * Credits a real-money Gem purchase. Idempotent per order id, so the server re-sending a grant
   * (snapshot replay, a failed acknowledgement, another device) never pays out twice.
   */
  creditPurchase(orderId: string, gems: number): boolean {
    if (!orderId || !(gems > 0) || this.state.purchases.includes(orderId)) return false;
    this.state.cells += Math.floor(gems);
    this.state.paidCells += Math.floor(gems);
    this.state.purchases.push(orderId);
    this.events.emit('purchaseCredited', { orderId, gems: Math.floor(gems) });
    return true;
  }

  grantOffline(seconds: number): Decimal {
    const { capSec, efficiency, minSec } = BALANCE.offline;
    const secs = Math.min(capSec, seconds);
    const dps = this.heroDpsTotal();
    if (secs < minSec || dps.lte(0)) return ZERO;
    const eff = efficiency + this.stats.add('offlineEfficiency');
    const kills = dps.mul(secs * eff).div(this.monsterHpAt(this.state.stage)).floor();
    const gold = kills.mul(this.goldAt(this.state.stage)).mul(this.stats.mult('gold'));
    if (gold.lte(0)) return ZERO;
    this.state.gold = this.state.gold.add(gold);
    this.events.emit('offline', { seconds: secs, gold });
    return gold;
  }

  /** Replace state wholesale (load, debug) and rebuild caches. */
  setState(state: GameState): void {
    this.state = state;
    this.invalidate();
    this.spawnMonster();
  }

  invalidate(): void {
    this.stats.invalidate();
    this.heroDpsCache = null;
  }

  // ---- Internals -----------------------------------------------------------------------------

  private spawnMonster(): void {
    const s = this.state;
    const boss = !s.farming && s.kills >= BALANCE.monstersPerStage;
    const hp = boss ? this.bossHpAt(s.stage) : this.monsterHpAt(s.stage);
    const seed = (s.stage * 7919 + s.kills * 104729 + (boss ? 13 : 0)) >>> 0;
    this.monster = { hp, maxHp: hp, boss, seed, dead: false };
    if (boss) this.bossTimeLeft = this.bossTimeSec();
    this.events.emit('spawn', { monster: this.monster });
  }

  /**
   * Apply a hit. Overkill splashes through: leftover damage carries into the next monsters
   * instantly (up to `splash.maxChain`), so overpowered players blast through stages instead of
   * being capped by respawn time.
   */
  private applyDamage(amount: Decimal, crit: boolean, source: DamageSource | null, pos?: Pos): void {
    let raw = amount;
    for (let chain = 0; ; chain++) {
      const m = this.monster;
      if (m.dead) return;
      const bossMult = m.boss ? this.stats.mult('bossDamage') : ONE;
      const dealt = raw.mul(bossMult);
      const hpBefore = m.hp;
      m.hp = m.hp.sub(dealt);
      if (source && chain === 0) this.events.emit('damage', { amount: dealt, crit, source, pos });
      if (m.hp.gt(0)) return;
      this.killMonster();
      if (chain + 1 >= BALANCE.splash.maxChain) return;
      // Leftover, converted back to un-boosted damage so boss multipliers don't compound.
      raw = dealt.sub(hpBefore).div(bossMult).mul(BALANCE.splash.efficiency);
      if (raw.lte(0)) return;
      this.spawnMonster();
      this.respawnIn = 0;
    }
  }

  private killMonster(): void {
    const m = this.monster, s = this.state;
    m.dead = true;
    m.hp = ZERO;
    const gold = this.killGold(m.boss);
    s.gold = s.gold.add(gold);
    s.lifetime.kills++;
    this.events.emit('kill', { monster: m, gold });

    if (m.boss) {
      s.lifetime.bossKills++;
      this.rollBossDrop(s.stage, s.stage >= s.bestStage);
      this.rollPetFood(s.stage, s.stage >= s.bestStage);
      s.stage++;
      s.kills = 0;
      s.farming = false;
      s.bestStage = Math.max(s.bestStage, s.stage);
      this.events.emit('stageCleared', { stage: s.stage });
    } else if (s.kills < BALANCE.monstersPerStage) {
      s.kills++;
    }
    this.respawnIn = BALANCE.monsterRespawnSec;
  }

  private failBoss(): void {
    this.state.farming = true;
    this.state.kills = BALANCE.monstersPerStage;
    this.events.emit('bossFailed', {});
    this.spawnMonster();
  }
}
